import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import Hls from 'hls.js';
import { VRButton } from 'three/examples/jsm/webxr/VRButton.js';
import { XRControllerModelFactory } from 'three/examples/jsm/webxr/XRControllerModelFactory.js';

const QUALITY_SETTINGS = {
  auto: { label: 'Auto', pixelRatio: -1, segments: 80, height: null },
  high: { label: 'Alta (4K)', pixelRatio: 2, segments: 100, height: 2160 },
  medium: { label: 'Media (1080p)', pixelRatio: 1.5, segments: 64, height: 1080 },
  low: { label: 'Baja (720p)', pixelRatio: 1, segments: 48, height: 720 },
};

const Video360Player = ({ videoUrl, posterUrl, title, onVideoEnd, stereoFormat = 'mono' }) => {
  // stereoFormat: 'mono' (normal 360), 'sbs' (side-by-side stereo), 'tb' (top-bottom stereo)
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const rendererRef = useRef(null);
  const frameIdRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const textureRef = useRef(null);
  const cleanupFnRef = useRef(null);
  const sphereRef = useRef(null);
  const hlsRef = useRef(null);
  const vrButtonRef = useRef(null);
  const vrUIRef = useRef(null);
  const controllerRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const tempMatrixRef = useRef(new THREE.Matrix4());
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buffered, setBuffered] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [quality, setQuality] = useState('auto');
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [hlsLevels, setHlsLevels] = useState([]);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [vrSupported, setVrSupported] = useState(false);
  const [isInVR, setIsInVR] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [detectedStereo, setDetectedStereo] = useState(false);
  
  const lonRef = useRef(0);
  const latRef = useRef(0);
  const isDraggingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const mountedRef = useRef(false);

  // Safe WebXR check - avoids SecurityError in iframes
  const isXRAvailable = useCallback(() => {
    try { return !!navigator.xr; } catch (e) { return false; }
  }, []);

  // Check for WebXR VR support
  useEffect(() => {
    if (!isXRAvailable()) return;
    try {
      navigator.xr.isSessionSupported('immersive-vr').then((supported) => {
        setVrSupported(supported);
      }).catch(() => setVrSupported(false));
    } catch (e) {
      setVrSupported(false);
    }
  }, [isXRAvailable]);

  // Create VR UI Panel with interactive buttons
  const createVRUI = useCallback((scene, renderer) => {
    const vrUI = new THREE.Group();
    vrUI.visible = false;
    vrUIRef.current = vrUI;

    // Panel background
    const panelGeometry = new THREE.PlaneGeometry(2, 1.2);
    const panelMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide
    });
    const panel = new THREE.Mesh(panelGeometry, panelMaterial);
    vrUI.add(panel);

    // Title text using canvas texture
    const createTextTexture = (text, fontSize = 64, color = '#ffffff', bgColor = null) => {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 128;
      const ctx = canvas.getContext('2d');
      if (bgColor) {
        ctx.fillStyle = bgColor;
        ctx.roundRect(0, 0, 512, 128, 20);
        ctx.fill();
      }
      ctx.fillStyle = color;
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 256, 64);
      const texture = new THREE.CanvasTexture(canvas);
      return texture;
    };

    // "Video Finalizado" title
    const titleGeometry = new THREE.PlaneGeometry(1.6, 0.3);
    const titleMaterial = new THREE.MeshBasicMaterial({
      map: createTextTexture('Video Finalizado', 48),
      transparent: true
    });
    const titleMesh = new THREE.Mesh(titleGeometry, titleMaterial);
    titleMesh.position.set(0, 0.35, 0.01);
    vrUI.add(titleMesh);

    // Exit button (red)
    const exitBtnGeometry = new THREE.PlaneGeometry(0.7, 0.35);
    const exitBtnMaterial = new THREE.MeshBasicMaterial({
      map: createTextTexture('SALIR', 40, '#ffffff', '#dc2626'),
      transparent: true
    });
    const exitBtn = new THREE.Mesh(exitBtnGeometry, exitBtnMaterial);
    exitBtn.position.set(-0.45, -0.15, 0.01);
    exitBtn.userData = { action: 'exit', isButton: true };
    vrUI.add(exitBtn);

    // Replay button (green)
    const replayBtnGeometry = new THREE.PlaneGeometry(0.7, 0.35);
    const replayBtnMaterial = new THREE.MeshBasicMaterial({
      map: createTextTexture('REPETIR', 40, '#ffffff', '#16a34a'),
      transparent: true
    });
    const replayBtn = new THREE.Mesh(replayBtnGeometry, replayBtnMaterial);
    replayBtn.position.set(0.45, -0.15, 0.01);
    replayBtn.userData = { action: 'replay', isButton: true };
    vrUI.add(replayBtn);

    // Position panel in front of user
    vrUI.position.set(0, 1.6, -3);

    scene.add(vrUI);

    // Setup controller for interaction
    const controller = renderer.xr.getController(0);
    controllerRef.current = controller;
    
    // Controller visual (ray line)
    const lineGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, -5)
    ]);
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0xffffff });
    const line = new THREE.Line(lineGeometry, lineMaterial);
    line.visible = false;
    controller.add(line);
    
    // Controller model
    const controllerModelFactory = new XRControllerModelFactory();
    const controllerGrip = renderer.xr.getControllerGrip(0);
    controllerGrip.add(controllerModelFactory.createControllerModel(controllerGrip));
    scene.add(controllerGrip);
    scene.add(controller);

    // Handle controller select (trigger press)
    const onSelect = () => {
      if (!vrUIRef.current?.visible) return;
      
      tempMatrixRef.current.identity().extractRotation(controller.matrixWorld);
      raycasterRef.current.ray.origin.setFromMatrixPosition(controller.matrixWorld);
      raycasterRef.current.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrixRef.current);

      const buttons = vrUIRef.current.children.filter(c => c.userData?.isButton);
      const intersects = raycasterRef.current.intersectObjects(buttons);

      if (intersects.length > 0) {
        const action = intersects[0].object.userData.action;
        console.log('VR button pressed:', action);
        
        if (action === 'exit') {
          // Exit VR and go back
          const session = renderer.xr.getSession();
          if (session) {
            session.end().then(() => {
              if (onVideoEnd) onVideoEnd();
              else window.history.back();
            });
          }
        } else if (action === 'replay') {
          // Replay video
          if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play();
            vrUIRef.current.visible = false;
            line.visible = false;
            setVideoEnded(false);
          }
        }
      }
    };

    controller.addEventListener('select', onSelect);

    // Show ray line when UI is visible
    const updateControllerVisual = () => {
      if (line && vrUIRef.current) {
        line.visible = vrUIRef.current.visible;
      }
    };

    return { vrUI, controller, line, updateControllerVisual, onSelect };
  }, [onVideoEnd]);

  // Initialize Three.js scene
  const initThreeJS = useCallback((video) => {
    if (!containerRef.current || rendererRef.current) return null;
    
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(75, width / height, 1, 1100);
    camera.position.set(0, 0, 0);
    cameraRef.current = camera;

    // Renderer with WebXR support
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      powerPreference: 'high-performance',
      alpha: false
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    try { renderer.xr.enabled = true; } catch (e) { /* XR not available */ }
    
    // Color management for accurate video colors
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping; // Preserve original video colors
    
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Store current XR session reference for cleanup
    let currentSession = null;

    // Add VR Button if supported
    if (isXRAvailable()) {
      try {
        navigator.xr.isSessionSupported('immersive-vr').then((supported) => {
          if (supported) {
            const vrButton = VRButton.createButton(renderer);
            vrButton.style.position = 'absolute';
            vrButton.style.bottom = '80px';
            vrButton.style.left = '50%';
            vrButton.style.transform = 'translateX(-50%)';
            vrButton.style.zIndex = '100';
            vrButton.style.background = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
            vrButton.style.border = 'none';
            vrButton.style.borderRadius = '25px';
            vrButton.style.padding = '12px 24px';
            vrButton.style.fontWeight = 'bold';
            vrButton.style.fontSize = '14px';
            container.appendChild(vrButton);
            vrButtonRef.current = vrButton;
            
            // Create VR UI (floating panel for when video ends)
            const vrUISetup = createVRUI(scene, renderer);
            
            // Track VR session state
            renderer.xr.addEventListener('sessionstart', () => {
              currentSession = renderer.xr.getSession();
              setIsInVR(true);
              
              // Hide VR UI initially
              if (vrUIRef.current) {
                vrUIRef.current.visible = false;
              }
              
              // Listen for session end from Meta menu button
              if (currentSession) {
                currentSession.addEventListener('end', () => {
                  setIsInVR(false);
                  currentSession = null;
                });
                
                // Handle visibility change (when user opens Meta menu)
                currentSession.addEventListener('visibilitychange', (event) => {
                  // VR visibility changed
                });
              }
            });
            
            renderer.xr.addEventListener('sessionend', () => {
              setIsInVR(false);
              currentSession = null;
              if (vrUIRef.current) {
                vrUIRef.current.visible = false;
              }
            });
          }
        }).catch(() => {});
      } catch (e) {
        // WebXR blocked by permissions policy
      }
    }

    // Video Texture with proper color space
    const texture = new THREE.VideoTexture(video);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.format = THREE.RGBAFormat;
    texture.generateMipmaps = false;
    texture.colorSpace = THREE.SRGBColorSpace; // Correct color space for video
    textureRef.current = texture;

    // Sphere geometry (inverted for 360 view) - higher resolution for better quality
    const geometry = new THREE.SphereGeometry(500, 80, 60);
    geometry.scale(-1, 1, 1);
    
    // Adjust UV mapping for stereo formats (use only left eye)
    // This is determined by the stereoFormat prop or auto-detected
    const effectiveStereoFormat = stereoFormat;
    
    if (effectiveStereoFormat === 'sbs') {
      // Side-by-side stereo: use left half (0 to 0.5 on U axis)
      const uvAttribute = geometry.attributes.uv;
      for (let i = 0; i < uvAttribute.count; i++) {
        const u = uvAttribute.getX(i);
        uvAttribute.setX(i, u * 0.5); // Scale U to use only left half
      }
      uvAttribute.needsUpdate = true;
      console.log('Applied SBS stereo UV mapping (using left eye)');
    } else if (effectiveStereoFormat === 'tb') {
      // Top-bottom stereo: use top half (0.5 to 1 on V axis)
      const uvAttribute = geometry.attributes.uv;
      for (let i = 0; i < uvAttribute.count; i++) {
        const v = uvAttribute.getY(i);
        uvAttribute.setY(i, v * 0.5 + 0.5); // Scale V to use only top half
      }
      uvAttribute.needsUpdate = true;
      console.log('Applied TB stereo UV mapping (using top eye)');
    }
    
    const material = new THREE.MeshBasicMaterial({ 
      map: texture,
      side: THREE.DoubleSide
    });
    
    const sphere = new THREE.Mesh(geometry, material);
    scene.add(sphere);
    sphereRef.current = sphere;

    // Render loop - supports both regular and VR rendering
    renderer.setAnimationLoop(() => {
      if (!rendererRef.current || !mountedRef.current) return;
      
      // Only update camera rotation when not in VR (VR handles its own camera)
      if (!renderer.xr?.isPresenting) {
        const lat = Math.max(-85, Math.min(85, latRef.current));
        const phi = THREE.MathUtils.degToRad(90 - lat);
        const theta = THREE.MathUtils.degToRad(lonRef.current);

        camera.lookAt(
          500 * Math.sin(phi) * Math.cos(theta),
          500 * Math.cos(phi),
          500 * Math.sin(phi) * Math.sin(theta)
        );
      }

      renderer.render(scene, camera);
    });

    // Mouse/Touch handlers
    const canvas = renderer.domElement;
    canvas.style.cursor = 'grab';

    const onMouseDown = (e) => {
      isDraggingRef.current = true;
      lastPosRef.current = { x: e.clientX, y: e.clientY };
      canvas.style.cursor = 'grabbing';
    };

    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      lonRef.current -= (e.clientX - lastPosRef.current.x) * 0.15;
      latRef.current += (e.clientY - lastPosRef.current.y) * 0.15;
      lastPosRef.current = { x: e.clientX, y: e.clientY };
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      canvas.style.cursor = 'grab';
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        isDraggingRef.current = true;
        lastPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const onTouchMove = (e) => {
      if (!isDraggingRef.current || e.touches.length !== 1) return;
      lonRef.current -= (e.touches[0].clientX - lastPosRef.current.x) * 0.15;
      latRef.current += (e.touches[0].clientY - lastPosRef.current.y) * 0.15;
      lastPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };

    const onTouchEnd = () => {
      isDraggingRef.current = false;
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseUp);
    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', onTouchMove, { passive: true });
    canvas.addEventListener('touchend', onTouchEnd);

    const onResize = () => {
      if (!containerRef.current || !rendererRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    // Return cleanup
    return () => {
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseleave', onMouseUp);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      
      // End VR session if active
      if (renderer.xr?.isPresenting) {
        const session = renderer.xr?.getSession();
        if (session) {
          session.end().catch(() => {});
        }
      }
      
      // Stop animation loop
      renderer.setAnimationLoop(null);
      
      // Remove VR button if exists
      if (vrButtonRef.current && container.contains(vrButtonRef.current)) {
        container.removeChild(vrButtonRef.current);
      }
      
      texture?.dispose();
      geometry?.dispose();
      material?.dispose();
      renderer?.dispose();
      
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [createVRUI, onVideoEnd]);

  // Main effect
  useEffect(() => {
    if (!videoUrl || !containerRef.current) return;

    mountedRef.current = true;
    let video = null;
    
    // Create video element
    video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.preload = 'metadata';
    video.muted = false;
    video.loop = false;
    videoRef.current = video;

    let hasMetadata = false;
    let hadError = false;

    video.onloadedmetadata = () => {
      if (!mountedRef.current) return;
      hasMetadata = true;
      console.log('Video metadata loaded:', video.duration, 'seconds');
      setDuration(video.duration);
      setIsReady(true);
      // Clear any previous error since video is working
      setError(null);
      // Initialize Three.js
      cleanupFnRef.current = initThreeJS(video);
    };

    video.oncanplay = () => {
      if (!mountedRef.current) return;
      console.log('Video can play');
      setIsLoading(false);
      setError(null);
    };

    video.onwaiting = () => {
      if (mountedRef.current) setIsLoading(true);
    };

    video.onplaying = () => {
      if (mountedRef.current) setIsLoading(false);
    };

    video.ontimeupdate = () => {
      if (mountedRef.current) setCurrentTime(video.currentTime);
    };

    video.onprogress = () => {
      if (!mountedRef.current) return;
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const dur = video.duration || 1;
        setBuffered(Math.round((bufferedEnd / dur) * 100));
      }
    };

    video.onended = () => {
      if (mountedRef.current) {
        setIsPlaying(false);
        setVideoEnded(true);
        
        // If in VR mode, show the VR UI panel
        if (rendererRef.current?.xr?.isPresenting && vrUIRef.current) {
          console.log('Video ended in VR - showing exit panel');
          vrUIRef.current.visible = true;
        } else if (!rendererRef.current?.xr?.isPresenting) {
          // Not in VR - call onVideoEnd callback if provided
          if (onVideoEnd) {
            onVideoEnd();
          }
        }
      }
    };

    video.onerror = (e) => {
      // Only log errors if metadata hasn't loaded yet
      // HLS.js can cause temporary errors during initialization that are not fatal
      if (!hasMetadata) {
        console.warn('Video error event (before metadata):', e);
      }
      // Don't set error state - let HLS.js handle recovery
      // The video will work even if some initial requests fail
    };

    // Set source - check if HLS stream
    console.log('Setting video source:', videoUrl.substring(0, 80) + '...');
    
    const isHLS = videoUrl.includes('.m3u8') || videoUrl.includes('cloudflarestream.com');
    
    if (isHLS && Hls.isSupported()) {
      // Use HLS.js for adaptive streaming
      console.log('Using HLS.js for adaptive streaming');
      
      // Clear any existing src before HLS attaches
      video.removeAttribute('src');
      
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        maxBufferLength: 120,        // Increased buffer for smoother 4K playback
        maxMaxBufferLength: 240,     // Allow larger buffer for high quality
        startLevel: -1,              // Auto-select best quality based on bandwidth
        autoStartLoad: true,
        capLevelToPlayerSize: false, // Don't limit quality based on player size (we're mapping to 360°)
        maxLoadingDelay: 4,
        minAutoBitrate: 0,           // Allow lowest quality as fallback
        abrEwmaDefaultEstimate: 5000000, // Assume decent bandwidth initially (5 Mbps)
        abrBandWidthFactor: 0.95,    // Use 95% of measured bandwidth
        abrBandWidthUpFactor: 0.7,   // Aggressive quality upgrades
      });
      
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
      
      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        console.log('HLS manifest parsed, levels:', data.levels.length);
        setHlsLevels(data.levels.map((level, index) => ({
          index,
          height: level.height,
          bitrate: level.bitrate
        })));
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('HLS fatal error:', data);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          }
        }
      });
      
      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = videoUrl;
    } else {
      // Regular video source
      video.src = videoUrl;
    }

    // Timeout for initial load - show error only if nothing happens in 30 seconds
    const loadTimeout = setTimeout(() => {
      if (!hasMetadata && mountedRef.current) {
        setError('El video está tardando demasiado en cargar. Verifica tu conexión.');
        setIsLoading(false);
      }
    }, 30000);

    // Cleanup
    return () => {
      mountedRef.current = false;
      clearTimeout(loadTimeout);
      
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
        frameIdRef.current = null;
      }
      
      if (cleanupFnRef.current) {
        cleanupFnRef.current();
        cleanupFnRef.current = null;
      }
      
      if (video) {
        video.pause();
        video.src = '';
        video.load();
      }
      
      videoRef.current = null;
      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      textureRef.current = null;
    };
  }, [videoUrl, initThreeJS]);

  // Play/Pause
  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (isPlaying) {
        video.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        await video.play();
        setIsPlaying(true);
      }
    } catch (err) {
      console.error('Play error:', err);
      // Try muted autoplay
      try {
        video.muted = true;
        await video.play();
        setIsPlaying(true);
        setTimeout(() => { video.muted = false; }, 100);
      } catch (err2) {
        setError('No se pudo reproducir. Intenta de nuevo.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Seek
  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    video.currentTime = pct * duration;
  };

  // Quality change
  const handleQualityChange = (newQuality) => {
    setQuality(newQuality);
    setShowQualityMenu(false);
    
    const settings = QUALITY_SETTINGS[newQuality];
    
    // Update renderer pixel ratio
    if (rendererRef.current) {
      const pixelRatio = settings.pixelRatio === -1 
        ? Math.min(window.devicePixelRatio, 2) 
        : settings.pixelRatio;
      rendererRef.current.setPixelRatio(pixelRatio);
    }
    
    // Force HLS to use specific quality level
    if (hlsRef.current && hlsLevels.length > 0) {
      if (newQuality === 'auto') {
        // Auto mode - let HLS.js decide
        hlsRef.current.currentLevel = -1;
        hlsRef.current.nextLevel = -1;
        console.log('HLS: Auto quality mode enabled');
      } else {
        // Find the best matching level for the requested quality
        const targetHeight = settings.height || 2160; // Default to 4K
        let bestLevel = hlsLevels.length - 1; // Start with highest
        
        for (let i = hlsLevels.length - 1; i >= 0; i--) {
          if (hlsLevels[i].height <= targetHeight) {
            bestLevel = hlsLevels[i].index;
            break;
          }
        }
        
        // Force this level
        hlsRef.current.currentLevel = bestLevel;
        hlsRef.current.nextLevel = bestLevel;
        console.log(`HLS: Forced level ${bestLevel} (${hlsLevels[bestLevel]?.height}p)`);
      }
    }
    
    // Update sphere geometry for quality
    if (sphereRef.current && sceneRef.current) {
      const oldSphere = sphereRef.current;
      const texture = textureRef.current;
      
      // Create new geometry with different segment count
      const newGeometry = new THREE.SphereGeometry(500, settings.segments, Math.round(settings.segments * 0.67));
      newGeometry.scale(-1, 1, 1);
      
      const newMaterial = new THREE.MeshBasicMaterial({ 
        map: texture,
        side: THREE.DoubleSide
      });
      
      const newSphere = new THREE.Mesh(newGeometry, newMaterial);
      
      sceneRef.current.remove(oldSphere);
      sceneRef.current.add(newSphere);
      sphereRef.current = newSphere;
      
      // Dispose old resources
      oldSphere.geometry.dispose();
      oldSphere.material.dispose();
    }
  };

  // Volume control
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      videoRef.current.muted = newVolume === 0;
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
      if (!newMuted && volume === 0) {
        setVolume(0.5);
        videoRef.current.volume = 0.5;
      }
    }
  };

  // Exit VR mode
  const exitVR = async () => {
    if (rendererRef.current?.xr?.isPresenting) {
      try {
        const session = rendererRef.current.xr.getSession();
        if (session) {
          await session.end();
        }
      } catch (error) {
        console.log('Error ending VR session:', error);
      }
    }
  };

  // Toggle VR mode
  const toggleVR = () => {
    if (isInVR) {
      exitVR();
    } else if (vrButtonRef.current) {
      vrButtonRef.current.click();
    }
  };

  // Format time
  const formatTime = (sec) => {
    if (!sec || isNaN(sec)) return '0:00';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Fullscreen
  const toggleFullscreen = () => {
    const el = containerRef.current?.parentElement;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen().catch(() => {});
    }
  };

  return (
    <div 
      data-testid="video-360-player"
      className="relative w-full bg-black rounded-xl overflow-hidden" 
      style={{ height: '500px' }}
    >
      <div ref={containerRef} className="w-full h-full" />

      {/* Loading */}
      {isLoading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none z-10">
          <div className="text-center text-white">
            <div className="w-12 h-12 border-4 border-white/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm">Cargando video 360°...</p>
            {buffered > 0 && buffered < 100 && (
              <p className="text-xs text-white/60 mt-2">Buffer: {buffered}%</p>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-20">
          <div className="text-center text-white p-6">
            <p className="text-red-400 mb-4">{error}</p>
            <button 
              onClick={() => {
                setError(null);
                setIsLoading(true);
                if (videoRef.current) {
                  videoRef.current.load();
                }
              }}
              className="px-6 py-2 bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Controls */}
      {!error && (
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent z-10">
          {/* Progress */}
          <div 
            className="h-1.5 bg-white/20 rounded-full cursor-pointer mb-3 group relative"
            onClick={handleSeek}
          >
            <div 
              className="absolute h-full bg-white/30 rounded-full transition-all"
              style={{ width: `${buffered}%` }}
            />
            <div 
              className="h-full bg-amber-500 rounded-full relative z-10 transition-all"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
            </div>
          </div>

          {/* Controls row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="play-pause-btn"
                onClick={togglePlay}
                disabled={isLoading && !isReady}
                className="w-10 h-10 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 rounded-full flex items-center justify-center text-white transition-colors"
              >
                {isPlaying ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6zm8 0h4v16h-4z"/>
                  </svg>
                ) : (
                  <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>
              
              {/* Volume control */}
              <div className="flex items-center gap-1 group">
                <button
                  data-testid="mute-btn"
                  onClick={toggleMute}
                  className="text-white/70 hover:text-white p-1 transition-colors"
                >
                  {isMuted || volume === 0 ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                    </svg>
                  ) : volume < 0.5 ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-0 group-hover:w-16 transition-all duration-200 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer opacity-0 group-hover:opacity-100 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
                />
              </div>
              
              <span className="text-white text-sm tabular-nums">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Quality selector */}
              <div className="relative">
                <button
                  data-testid="quality-btn"
                  onClick={() => setShowQualityMenu(!showQualityMenu)}
                  className="flex items-center gap-1 text-white/70 hover:text-white px-2 py-1 rounded transition-colors text-sm"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                  </svg>
                  <span className="hidden sm:inline">{QUALITY_SETTINGS[quality].label}</span>
                </button>
                
                {showQualityMenu && (
                  <div className="absolute bottom-full right-0 mb-2 bg-black/95 rounded-lg shadow-xl overflow-hidden min-w-[140px]">
                    {Object.entries(QUALITY_SETTINGS).map(([key, settings]) => (
                      <button
                        key={key}
                        onClick={() => handleQualityChange(key)}
                        className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                          quality === key 
                            ? 'bg-amber-600 text-white' 
                            : 'text-white/80 hover:bg-white/10'
                        }`}
                      >
                        {settings.label}
                        {quality === key && (
                          <svg className="w-4 h-4 inline ml-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* VR Mode Button */}
              {vrSupported && (
                <button
                  data-testid="vr-mode-btn"
                  onClick={toggleVR}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    isInVR 
                      ? 'bg-red-500 text-white hover:bg-red-600' 
                      : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700'
                  }`}
                >
                  {isInVR ? (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                      </svg>
                      <span className="hidden sm:inline">Salir VR</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.74 6H3.21C1.99 6 1 6.99 1 8.21v7.58C1 17.01 1.99 18 3.21 18h5.35c.63 0 1.22-.35 1.52-.91l.81-1.52c.26-.49.71-.79 1.22-.79h1.78c.51 0 .97.3 1.22.79l.81 1.52c.3.56.88.91 1.52.91h5.35c1.22 0 2.21-.99 2.21-2.21V8.21C23 6.99 22.01 6 20.74 6zM7.75 14c-1.24 0-2.25-1.01-2.25-2.25S6.51 9.5 7.75 9.5 10 10.51 10 11.75 8.99 14 7.75 14zm8.5 0c-1.24 0-2.25-1.01-2.25-2.25s1.01-2.25 2.25-2.25 2.25 1.01 2.25 2.25S17.49 14 16.25 14z"/>
                      </svg>
                      <span className="hidden sm:inline">Meta VR</span>
                    </>
                  )}
                </button>
              )}
              
              <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded-full font-medium">
                360°
              </span>
              <button
                data-testid="fullscreen-btn"
                onClick={toggleFullscreen}
                className="text-white/70 hover:text-white p-1.5 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              </button>
            </div>
          </div>
          
          <p className="text-white/40 text-xs text-center mt-2">
            Arrastra para ver en 360° {vrSupported && '• Usa Meta Quest para experiencia inmersiva'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Video360Player;
